import { useState, useEffect, useCallback, useRef } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { isTrialWindowActive, FREE_LIMITS, LITE_LIMITS } from "@/lib/featureGating";

export interface Subscription {
  id: string;
  user_id: string;
  stripe_customer_id: string;
  stripe_subscription_id?: string;
  // 'trial' and 'no-subscription' are not written by current code paths, but
  // legacy rows from the old trial-code system and pre-plan-migration accounts
  // still carry them at runtime — widened here so comparisons against them
  // stay real guards instead of TypeScript proving the branch unreachable.
  plan: "free" | "lite" | "standard" | "premium" | "pro" | "enterprise" | "trial" | "no-subscription";
  period?: "monthly" | "yearly" | "onetime"; // 'onetime' = Pro/Enterprise 1-year prepaid, no subscription
  trial_started_at?: string;
  trial_ends_at?: string;
  current_period_start?: string;
  current_period_end?: string;
  /** 'trialing' = card-upfront free trial, full plan entitlement until trial_ends_at. */
  /** 'paused' = a pause chosen instead of cancelling: free plan, projects kept,
   *  until paused_until (supabase/functions/subscription-retention). */
  status: "active" | "trialing" | "canceled" | "past_due" | "incomplete" | "incomplete_expired" | "unpaid" | "paused";
  /** Set while a pause is scheduled or running. */
  paused_from?: string | null;
  paused_until?: string | null;
  cancel_at_period_end?: boolean;
  /** Purchased extra team seats above the plan's included base (Enterprise per-seat
   *  overage). 0/undefined for everyone else. Effective cap = base(plan) + extra_seats. */
  extra_seats?: number;
  /** Bespoke team size for an enterprise row (Enterprise Duo = 2). null/absent
   *  means the plan's own default of 11, and its presence is the ONLY thing
   *  that distinguishes a Duo subscription from full Enterprise. */
  included_seats?: number | null;
  /** Who bills the plan: the website (Stripe) or an app store. A store-billed
   *  plan is managed in that store, never through the Stripe portal. */
  billing_source?: 'stripe' | 'app_store' | 'play';
  created_at: string;
  updated_at: string;
}

interface UseSubscriptionOptions {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

// Plan precedence for merging a user's own plan with an inherited org-seat plan.
const PLAN_RANK: Record<string, number> = {
  free: 0, lite: 1, standard: 2, premium: 3, pro: 4, enterprise: 5,
};

// The statuses that confer the plan's entitlements. There is no fourth one:
// `past_due` means free from the moment Stripe writes it, matching
// `effective_plan_for()` in the database, which is what RLS actually enforces.
// See the note where the grace period used to live in featureGating.ts.
const ENTITLED_STATUSES = ["active", "trialing"] as const;

/**
 * Whether exports carry the "made with cctvplanner.io" watermark during a paid
 * card-upfront trial. OFF by design: the trial is meant to be the full product,
 * and 3 days is short enough that "do the job, then leave" is a small risk.
 * Flip to true to stamp trial exports: every watermark site reads
 * shouldWatermark(), so this one constant is the whole switch.
 */
const WATERMARK_DURING_TRIALING = false;

export const useSubscription = ({ user, session, loading }: UseSubscriptionOptions) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  // True when the user is covered by an org seat (a teammate-owner's Pro/Enterprise
  // licence). Such members must NOT buy their own subscription — gated in the UI
  // and authoritatively in the checkout edge functions.
  const [isOrgSeat, setIsOrgSeat] = useState(false);
  // False until the FIRST resolution of this user's subscription (own row merged
  // with any org seat) lands, including the "no row = free" outcome. Purchase
  // surfaces gate on this: while it's pending they render disabled CTAs instead
  // of guessing "free", which is how paying customers used to see "Start plan" +
  // trial buttons for a moment and click their way into a 409.
  const [subscriptionResolved, setSubscriptionResolved] = useState(false);
  const isMountedRef = useRef(true);
  // The plan this tab last resolved, and whether it came from an org seat. A
  // re-read that fails keeps it: a dropped request says nothing about the plan,
  // and reading it as "no plan" turned a paying customer's open project into
  // the free plan's read-only preview (Studio customer, 14.10, the moment a
  // second tab opened and every tab re-read the plan).
  // `known` = read from the server, not assumed after a failed first read;
  // `ownRow` = the user's own row was there.
  const resolvedRef = useRef<{ userId: string; orgSeat: boolean; known: boolean; ownRow: boolean } | null>(null);
  // A first read that failed is tried again later, so a tab opened offline
  // finds the paid plan once the connection is back.
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchRef = useRef<((userId: string) => Promise<void>) | null>(null);

  // A different account starts unresolved again.
  useEffect(() => {
    setSubscriptionResolved(false);
  }, [user?.id]);

  // Component-lifetime mount flag. Previously this ref was flipped inside
  // the realtime effect's cleanup, which broke the visibility-refresh
  // and other effects that share the same ref: after the realtime effect
  // re-ran (e.g. user.id changed), isMountedRef stayed false for the
  // remainder of the component's life. Decoupling fixes that.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  // Org-licence inheritance: Enterprise members always inherit the owner's tier
  // (any role, that's the paid product — 10+ seats each with full entitlement).
  // Pro members only inherit if they hold an editor/admin role AND fall within
  // the owner's purchased extra_seats count (see my_org_seat_plan) — a free/
  // view-only Pro invitee needs their own subscription for feature access.
  // RLS hides the owner's row, so a SECURITY DEFINER RPC resolves the tier
  // server-side. null = no seat; undefined = the lookup failed.
  const resolveOrgSeatPlan = useCallback(async (): Promise<"pro" | "enterprise" | null | undefined> => {
    try {
      const { data, error } = await supabase.rpc("my_org_seat_plan" as never);
      if (error) return undefined;
      return data === "enterprise" || data === "pro" ? data : null;
    } catch {
      return undefined;
    }
  }, []);

  // Merge the user's OWN subscription with any inherited org seat, keeping the
  // higher tier, then set state. Downstream gating reads `subscription.plan`, so
  // a seated member gets a synthetic active subscription at the org tier and
  // every entitlement check works unchanged.
  const applyEffectiveSubscription = useCallback(
    async (userId: string, ownSub: Subscription | null, known = true) => {
      const ownPlan = ownSub && (ENTITLED_STATUSES as readonly string[]).includes(ownSub.status)
        ? ownSub.plan
        : "free";
      const orgPlan = await resolveOrgSeatPlan();
      if (!isMountedRef.current) return;
      // A seated member whose seat lookup failed keeps the seat they had.
      if (orgPlan === undefined && resolvedRef.current?.userId === userId && resolvedRef.current.orgSeat) return;
      resolvedRef.current = { userId, orgSeat: !!orgPlan, known, ownRow: !!ownSub };
      setIsOrgSeat(!!orgPlan);
      if (orgPlan && PLAN_RANK[orgPlan] > (PLAN_RANK[ownPlan] ?? 0)) {
        setSubscription({
          id: ownSub?.id ?? `org-seat:${userId}`,
          user_id: userId,
          stripe_customer_id: ownSub?.stripe_customer_id ?? "",
          plan: orgPlan,
          status: "active",
          period: ownSub?.period,
          created_at: ownSub?.created_at ?? new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      } else {
        setSubscription(ownSub);
      }
      setSubscriptionResolved(true);
    },
    [resolveOrgSeatPlan],
  );

  const fetchSubscription = useCallback(async (userId: string) => {
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
    const known = () => resolvedRef.current?.userId === userId && resolvedRef.current.known;
    // The plan could not be read. A tab that already knows it keeps it; one
    // that never read it starts on the free plan and tries again later.
    const unreadable = async () => {
      if (known()) return;
      await applyEffectiveSubscription(userId, null, false);
      if (!isMountedRef.current) return;
      retryTimerRef.current = setTimeout(() => { void fetchRef.current?.(userId); }, 15000);
    };
    const readRow = () => supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();
    try {
      let { data, error } = await readRow();
      // A request lost on the way (offline for a moment, a dropped connection,
      // the session lock taken over by another tab) is tried again.
      for (const waitMs of [1000, 3000]) {
        if (!error || error.code === "PGRST116") break;
        await new Promise((resolve) => setTimeout(resolve, waitMs));
        if (!isMountedRef.current) return;
        ({ data, error } = await readRow());
      }

      if (!isMountedRef.current) return;

      // The table types plan, period and status as plain text; the webhooks
      // only ever write the values Subscription lists.
      let ownSub: Subscription | null = null;
      if (!error && data) {
        ownSub = data as Subscription;
      } else if (!error && known() && resolvedRef.current?.ownRow) {
        // A row read before does not vanish (only deleting the account removes
        // it): an empty answer here is a request that went out without the
        // user's session, not a lost plan.
        return;
      } else if (error) {
        if (error.code === "PGRST116") {
          // .maybeSingle() returns PGRST116 only when multiple rows match
          // (zero rows is silent). Webhook bugs in the past produced
          // duplicate subscription rows per user; prefer an active one
          // over a canceled/incomplete leftover, then fall back to latest.
          console.warn("Multiple subscription rows found for user, picking active or latest");
          const { data: rows } = await supabase
            .from("subscriptions")
            .select("*")
            .eq("user_id", userId)
            .order("updated_at", { ascending: false });
          if (!isMountedRef.current) return;
          if (rows && rows.length > 0) {
            ownSub = (rows.find((r) => r.status === "active")
              ?? rows.find((r) => r.status === "trialing")
              ?? rows[0]) as Subscription;
          }
        } else {
          console.warn("Subscription fetch error:", error.code);
          await unreadable();
          return;
        }
      }

      // Apply org-seat inheritance on top of the user's own plan.
      await applyEffectiveSubscription(userId, ownSub);
    } catch (err) {
      // Silently ignore AbortError - these are normal when navigation happens
      if (err instanceof Error && err.name === 'AbortError') {
        console.debug('Subscription fetch aborted');
        return;
      }
      console.error("Subscription fetch error:", err);
      // A failed fetch still ends the "unknown" state — leaving CTAs disabled
      // forever on a network blip would be worse than the old optimistic UI.
      if (isMountedRef.current) {
        setSubscriptionResolved(true);
        if (!known()) retryTimerRef.current = setTimeout(() => { void fetchRef.current?.(userId); }, 15000);
      }
    }
  }, [applyEffectiveSubscription]);

  fetchRef.current = fetchSubscription;

  const clearSubscription = useCallback(() => {
    resolvedRef.current = null;
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
    setSubscription(null);
    setIsOrgSeat(false);
  }, []);

  // Set up real-time listener for subscription updates.
  // Channel handle declared OUTSIDE the try block so the cleanup can
  // unsubscribe even if a later `.on(...)` call throws — otherwise the first
  // partially-constructed channel would stay subscribed for the rest of the
  // component's life on every subsequent re-mount.
  useEffect(() => {
    if (!user?.id) return;

    let channelHandle: ReturnType<typeof supabase.channel> | null = null;

    try {
      const channelName = `public:subscriptions:user_id=eq.${user.id}`;

      channelHandle = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
            schema: 'public',
            table: 'subscriptions',
            filter: `user_id=eq.${user.id}`,
          },
          (payload: any) => {
            // Realtime payloads can arrive empty when REPLICA IDENTITY isn't
            // set to FULL on the source table — payload.new ends up as `{}`
            // for UPDATE/INSERT, which would otherwise replace a real
            // subscription with an empty object and silently demote the user
            // to "free". Require the canonical fields before applying.
            const next = payload?.new;
            if (next && typeof next === "object" && next.id && next.user_id && next.plan && next.status) {
              // Route through the org-seat merge instead of setting state raw.
              // This payload is the user's OWN row, which for a seated member is
              // the `free` row that must not override the inherited org tier.
              // The 5-minute last_seen_at heartbeat below writes that very row,
              // so applying it directly demoted every Enterprise/Pro seat back
              // to free once per heartbeat, until some other path refetched.
              void applyEffectiveSubscription(next.user_id as string, next as Subscription);
              return;
            }
            // DELETE arrives with empty new + populated old — clear state.
            if (payload?.eventType === "DELETE" && payload?.old) {
              setSubscription(null);
              return;
            }
            // Anything else: refetch authoritative state instead of trusting
            // a partial payload. user.id is guaranteed present here.
            if (user?.id) fetchSubscription(user.id);
          }
        )
        .subscribe((status) => {
          if (status === 'CLOSED') {
            console.debug('Real-time subscription channel closed (may reconnect)');
          } else if (status === 'CHANNEL_ERROR') {
            console.debug('Real-time subscription channel error (may retry)');
          }
        });

      // Cleanup both happy and partial-construction paths.
      return () => {
        try { channelHandle?.unsubscribe(); } catch { /* ignore unsubscribe errors */ }
      };
    } catch (err) {
      console.debug('Error setting up real-time subscription:', err);
      return () => {
        try { channelHandle?.unsubscribe(); } catch { /* ignore unsubscribe errors */ }
      };
    }
  }, [user?.id, fetchSubscription, applyEffectiveSubscription]);

  // Heartbeat: update last_seen_at in subscriptions every 5 minutes
  useEffect(() => {
    if (!user?.id) return;

    const updateLastSeen = () => {
      supabase
        .from('subscriptions')
        .update({ last_seen_at: new Date().toISOString() } as Record<string, unknown>)
        .eq('user_id', user.id)
        .then(() => {}, (err: unknown) => {
          console.warn('Heartbeat update failed:', err instanceof Error ? err.message : err);
        });
    };

    updateLastSeen(); // immediately on login
    const interval = setInterval(updateLastSeen, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(interval);
  }, [user?.id]);

  // Refresh subscription when page becomes visible (user returns from Stripe checkout).
  // The pendingUpgrade flag is stored as `<expiresAtMs>` so a stale entry from a
  // closed tab (user abandoned Stripe) doesn't trigger a phantom refetch
  // weeks later. 30 minutes covers the longest realistic checkout flow.
  useEffect(() => {
    if (!user?.id) return;

    const PENDING_UPGRADE_TTL_MS = 30 * 60 * 1000;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible' || !isMountedRef.current) return;
      const raw = localStorage.getItem('pendingUpgrade');
      if (!raw) return;

      // Backward-compatible: legacy entries had no timestamp; treat them as
      // expired so we clear without firing an extra request.
      const expiresAt = Number(raw);
      const expired = !Number.isFinite(expiresAt) || expiresAt < Date.now();
      if (expired) {
        try { localStorage.removeItem('pendingUpgrade'); } catch { /* ignore */ }
        return;
      }

      await fetchSubscription(user.id);
      if (isMountedRef.current) {
        try { localStorage.removeItem('pendingUpgrade'); } catch { /* ignore */ }
      }
      // The TTL constant is referenced from the Stripe checkout call site
      // (whichever module calls `localStorage.setItem('pendingUpgrade', String(Date.now() + ttl))`).
      void PENDING_UPGRADE_TTL_MS;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, fetchSubscription]);

  // --- Subscription helper functions ---

  // Inside a card-upfront trial: the plan's entitlements apply in full, and a
  // charge is already authorized for when it ends. The free plan is NOT a trial
  // (it never expires), which is why this keys off status, not plan.
  const isTrialing = useCallback(() => {
    return subscription?.status === "trialing"
      && subscription.plan !== "free"
      && isTrialWindowActive(subscription.trial_ends_at);
  }, [subscription]);

  // Days left in a card-upfront trial (0 for everyone else). Status-gated on
  // purpose: the legacy trial-code flow writes trial_ends_at on rows whose
  // status is 'active', and those must keep reporting 0 as they always have.
  const getTrialDaysRemaining = useCallback(() => {
    if (subscription?.status !== "trialing" || !subscription.trial_ends_at) return 0;
    const endsAt = new Date(subscription.trial_ends_at);
    if (isNaN(endsAt.getTime())) return 0;
    return Math.max(0, Math.ceil((endsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  }, [subscription]);

  // True only in the narrow window where the trial has ended but the webhook
  // has not yet moved the row to active/past_due.
  const isTrialExpired = useCallback(() => {
    if (subscription?.status !== "trialing" || !subscription.trial_ends_at) return false;
    const endsAt = new Date(subscription.trial_ends_at);
    if (isNaN(endsAt.getTime())) return false;
    return Date.now() >= endsAt.getTime();
  }, [subscription]);

  const isLite = useCallback(() => {
    return subscription?.plan === "lite" && (subscription?.status === "active" || isTrialing());
  }, [subscription, isTrialing]);

  const isStandard = useCallback(() => {
    return subscription?.plan === "standard" && (subscription?.status === "active" || isTrialing());
  }, [subscription, isTrialing]);

  const isPremium = useCallback(() => {
    return subscription?.plan === "premium" && (subscription?.status === "active" || isTrialing());
  }, [subscription, isTrialing]);

  // "Pro or above": Enterprise is a strict superset of Pro, so every
  // `isPremium || isPro` feature gate (multi-floor, portal, validator, BOM,
  // DXF, smart-quote) passes for Enterprise too WITHOUT editing the
  // parallel-owned bag builders. Enterprise-only limits (10 seats, white-label,
  // unlimited companies) are resolved by plan name via featureGating helpers.
  const isPro = useCallback(() => {
    return (subscription?.plan === "pro" || subscription?.plan === "enterprise")
      && (subscription?.status === "active" || isTrialing());
  }, [subscription, isTrialing]);

  const isEnterprise = useCallback(() => {
    return subscription?.plan === "enterprise" && (subscription?.status === "active" || isTrialing());
  }, [subscription, isTrialing]);

  const isPaid = useCallback(() => {
    if (!subscription || subscription.plan === "free") return false;
    if (subscription.status === "active") return true;
    // A card-upfront trial is entitled like a paid plan: the card is on file and
    // the charge is authorized. Without this, featureGating would find neither
    // isPaid nor isTrial true and fall through to its "cannot verify" branch,
    // locking a trialing customer out of everything.
    if (subscription.status === "trialing" && isTrialWindowActive(subscription.trial_ends_at)) return true;
    // past_due is NOT paid, not even for a minute: the server already refuses
    // the plan's limits, so keeping the UI open here only produced features
    // that failed on save.
    return false;
  }, [subscription]);

  const isTrial = useCallback(() => {
    // While still loading, don't assume free plan
    if (!subscription && loading) return false;
    // No subscription record means free plan
    if (!subscription) return true;
    // Inactive subscriptions (incomplete, canceled, etc.) = free plan
    const inactiveStatuses = ['incomplete', 'incomplete_expired', 'canceled', 'unpaid', 'paused'];
    if (inactiveStatuses.includes(subscription.status)) return true;
    // past_due reverts to free immediately (no grace), same as the server.
    if (subscription.status === 'past_due') return true;
    // Safety net: a 'trialing' row whose window has fully lapsed (webhook never
    // flipped it) degrades to free-plan limits rather than to the "cannot verify
    // subscription" dead end, which blocks even the first camera.
    if (subscription.status === 'trialing' && !isTrialWindowActive(subscription.trial_ends_at)) return true;
    // A healthy trialing row is NOT a free user: it falls through to false here,
    // and isPaid() above returns true for it.
    // Explicit free plan
    return subscription.plan === "free" && subscription.status === "active";
  }, [subscription, loading]);

  // Single source of truth for export branding. Free users are watermarked;
  // trialing users only if WATERMARK_DURING_TRIALING is on. Every watermark
  // site in the app reads this, so the policy lives in exactly one place.
  const shouldWatermark = useCallback(() => {
    return (isTrial() && !isPaid()) || (WATERMARK_DURING_TRIALING && isTrialing());
  }, [isTrial, isPaid, isTrialing]);

  // Reads the shared constants rather than repeating the numbers. This function
  // is the Dashboard's limit (it gates "New project" and "Copy" via
  // canAddProject), while the designer gates on FREE_LIMITS through
  // canSaveProject and the server enforces get_project_limit_for_user. Three
  // readers, so the numbers must come from one place: on 2026-06-29 the free
  // cap went 1 -> 2 in FREE_LIMITS, the other two were left behind, and for six
  // weeks the app offered a second project it then refused to save.
  // `isTrial()` here means "effectively on the free tier" (no row, inactive,
  // lapsed), not "in a paid trial" — see its body.
  const getProjectLimit = useCallback(() => {
    if (isTrial()) return FREE_LIMITS.projects;
    if (isLite()) return LITE_LIMITS.projects;
    if (isStandard() || isPremium() || isPro()) return Infinity;
    // While subscription is still loading (user exists but subscription not yet fetched),
    // return free-tier default to avoid flash of "no access" state
    if (!subscription && user) return FREE_LIMITS.projects;
    return 0; // No access
  }, [subscription, user, isTrial, isLite, isStandard, isPremium, isPro]);

  const getOfferLimit = useCallback(() => {
    if (isTrial()) return 1;
    if (isLite()) return 1;
    if (isStandard()) return 1;
    if (isPremium() || isPro()) return Infinity;
    if (!subscription && user) return 1;
    return 0; // No access
  }, [subscription, user, isTrial, isLite, isStandard, isPremium, isPro]);

  const canGenerateOffers = useCallback(() => {
    return getOfferLimit() > 0;
  }, [getOfferLimit]);

  const getPlanName = useCallback(() => {
    const periodLabel = subscription?.period === "onetime"
      ? "1-Year"
      : subscription?.period === "yearly" ? "Yearly" : "Monthly";
    // During a trial the billing period is not what the customer is on yet, so
    // the label says "Trial" instead of Monthly/Yearly.
    const suffix = isTrialing() ? "Trial" : periodLabel;
    if (isTrial()) return "Free";
    if (isLite()) return `Lite (${suffix})`;
    if (isStandard()) return `Standard (${suffix})`;
    if (isPremium()) return `Premium (${suffix})`;
    // Enterprise must be checked before Pro: isPro() is true for both.
    if (isEnterprise()) return `Enterprise (${suffix})`;
    if (isPro()) return `Pro (${suffix})`;
    return "Free";
  }, [subscription, isTrial, isTrialing, isLite, isStandard, isPremium, isPro, isEnterprise]);

  return {
    subscription,
    isOrgSeat,
    // True while a signed-in user's entitlement is still unknown. Anonymous
    // visitors are never "loading" — they have nothing to resolve.
    subscriptionLoading: !!user && !subscriptionResolved,
    fetchSubscription,
    clearSubscription,
    getTrialDaysRemaining,
    isTrialExpired,
    isLite,
    isStandard,
    isPremium,
    isPro,
    isEnterprise,
    isPaid,
    isTrial,
    isTrialing,
    shouldWatermark,
    getProjectLimit,
    getOfferLimit,
    canGenerateOffers,
    getPlanName,
  };
};
